import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { publicApi } from "../lib/api";

/**
 * Public Sponsor Landing — Phase 1 shell.
 *
 * When a visitor lands via a QR code or /sponsor/:slug link, we:
 *   1. Fetch the sponsor's public branding (safe fields only).
 *   2. Fire an anonymous page_visit event (recorded by the fetch itself
 *      server-side).
 *   3. Persist the referral_code in localStorage so a subsequent
 *      /signup or SSO flow can call POST /api/sponsor/attribute to
 *      permanently link the new user to the sponsor.
 *
 * Attribution is NOT permanent from a mere visit — only an authenticated
 * signup that calls /api/sponsor/attribute promotes it.
 */
export default function SponsorLandingPage() {
  const { slug } = useParams();
  const [sponsor, setSponsor] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    publicApi
      .sponsorLanding(slug)
      .then((s) => {
        setSponsor(s);
        try {
          localStorage.setItem(
            "lg_sponsor_ref",
            JSON.stringify({
              referral_code: s.referral_code,
              sponsor_slug: s.sponsor_slug,
              at: Date.now(),
            })
          );
        } catch {
          // Non-fatal — attribution can still happen via query param on signup.
        }
      })
      .catch((e) =>
        setErr(e?.response?.status === 404 ? "Sponsor not found" : "Unable to load")
      );
  }, [slug]);

  const trackClick = () => {
    if (!sponsor) return;
    publicApi
      .sponsorTrack({
        referral_code: sponsor.referral_code,
        event_type: "referral_click",
        path: window.location.pathname,
      })
      .catch(() => {});
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#7C3E33]" data-testid="sponsor-landing-error">
        {err}
      </div>
    );
  }
  if (!sponsor) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">
        Loading…
      </div>
    );
  }

  const primary = sponsor.primary_color || "#4F6F52";

  return (
    <div className="min-h-screen text-[#2E2E2E]" data-testid="sponsor-landing-page">
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-2xl flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]"
              style={{ backgroundColor: primary }}
            >
              <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
            </div>
            <span className="font-display text-xl" style={{ color: primary }}>
              LifeGuide
            </span>
          </Link>
          <Link to="/login" className="text-sm text-[#6B6B6B] hover:text-[#4F6F52]">
            Sign in
          </Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-16 sm:pt-24 pb-16 text-center">
        {sponsor.logo_url && (
          <img
            data-testid="sponsor-logo"
            src={sponsor.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}` : sponsor.logo_url}
            alt={`${sponsor.sponsor_name} logo`}
            className="mx-auto mb-8 h-16 w-auto"
          />
        )}
        <div className="inline-block rounded-full px-4 py-1.5 mb-6 border" style={{ borderColor: `${primary}33`, color: primary, backgroundColor: `${primary}14` }}>
          <span className="text-xs uppercase tracking-[0.18em] font-semibold">
            In partnership with
          </span>
        </div>
        <h1
          data-testid="sponsor-landing-heading"
          className="font-display text-4xl sm:text-5xl leading-[1.05] mb-4"
        >
          {sponsor.sponsor_name} is making LifeGuide available to our community.
        </h1>
        <p
          data-testid="sponsor-landing-explainer"
          className="text-[17px] text-[#4E5A4C] leading-relaxed max-w-xl mx-auto mb-10"
        >
          LifeGuide is an AI-powered Christian Life Operating System with
          daily devotionals, crisis guidance, relationship coaching, Bible
          study, and spiritual growth tracking. Start your free 3-day Pro
          trial — no credit card required.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to={`/signup?ref=${encodeURIComponent(sponsor.referral_code)}`}
            onClick={trackClick}
            data-testid="sponsor-cta-signup"
            className="text-white rounded-full px-6 py-3 font-medium shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            style={{ backgroundColor: primary }}
          >
            Start free trial
          </Link>
          <Link
            to="/welcome"
            className="rounded-full px-6 py-3 font-medium border border-[#DCE5DB] hover:bg-[#EEF3EC]/60"
          >
            Learn more
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#DCE5DB] py-8 bg-white/50">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-[#6B6B6B] flex flex-wrap justify-center gap-x-5">
          <Link to="/sms" className="hover:text-[#4F6F52]">SMS Updates</Link>
          <Link to="/privacy" className="hover:text-[#4F6F52]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#4F6F52]">Terms</Link>
        </div>
      </footer>
    </div>
  );
}
